import type { ReactNode } from 'react';
import { memo } from 'react';
import { useDelayedCallback } from '@/hooks/useDelayedCallback';
import { Box, Grid, Text } from '@simplywallst/ui-core';
import { CompanyTile, CompanyTileSkeleton } from '@/components/CompanyTile';

const MemoTile = memo(CompanyTile);

const Skeleton = () =>
  new Array(24).fill(null).map((_, i) => <CompanyTileSkeleton key={i} />);

export type Company = {
  canonicalUrl: string;
  companyId: string;
  shortDescription: string;
  shortName: string;
  website: string;
  marketCapFormatted: string;
  snowflakePoints: [number, number, number, number, number];
  tickerSymbol: string;
  uniqueSymbol: string;
  return7DFormatted: string;
  return7D: number | null;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  sharePriceFormatted: string;
  peFormatted: string;
  growthEstimateFormatted: string;
  analystsPriceTargetFormatted: string;
  industryLabel: string;
  isRecentSearchEntry: boolean | null;
  hasLogo: boolean;
};

export type Props = {
  companies: Company[];
  isFetching: boolean;
  isFetched: boolean;
  onLongHover: (id: string) => void;
  noDataContent?: ReactNode;
};

export const TileList = ({
  companies,
  isFetching,
  isFetched,
  onLongHover,
  noDataContent,
}: Props) => {
  const [delayedCallback, cancelCallback] = useDelayedCallback<string>((id) =>
    onLongHover(id)
  );

  return (
    <div className="lg:mx-0">
      {!isFetching && isFetched && companies.length === 0 && (
        <>
          {noDataContent ?? (
            <Box margin="x3">
              <Grid justifyContent="center">
                <Text typography="xSmall" color="textSoft">
                  No companies found
                </Text>
              </Grid>
            </Box>
          )}
        </>
      )}
      <Grid
        gap="24px"
        templateColumns="1fr"
        templateColumnsSm="1fr 1fr"
        templateColumnsMd="repeat(3, 1fr)"
        templateColumnsXl="repeat(4, 1fr)"
        templateRows="1fr"
        data-cy-id="stock-tile"
      >
        {isFetched &&
          companies.map((company, index) => (
            <MemoTile
              index={index}
              key={`${company.companyId}-stock-tile`}
              companyName={company.shortName}
              companyId={company.companyId}
              canonicalUrl={company.canonicalUrl}
              uniqueSymbol={company.uniqueSymbol}
              description={company.shortDescription}
              website={company.website}
              marketCap={company.marketCapFormatted}
              return7D={company.return7D}
              return7DFormatted={company.return7DFormatted}
              return1Y={company.return1YrAbs}
              return1YFormatted={company.return1YrAbsFormatted}
              tickerSymbol={company.tickerSymbol}
              sharePrice={company.sharePriceFormatted}
              snowflakePoints={company.snowflakePoints}
              industry={company.industryLabel}
              peRatio={company.peFormatted}
              growthEstimate={company.growthEstimateFormatted}
              analystPriceTarget={company.analystsPriceTargetFormatted}
              isRecentSearchEntry={company.isRecentSearchEntry}
              hasLogo={company.hasLogo}
              onMouseOver={() => {
                delayedCallback(company.companyId);
              }}
              onMouseOut={cancelCallback}
              onFocus={() => delayedCallback(company.companyId)}
              onBlur={cancelCallback}
            />
          ))}
        {isFetching && <Skeleton />}
      </Grid>
    </div>
  );
};
